import type { ReactElement } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import type { Autocomplete } from "@similarweb/ui-components";
import { Checkbox, Tooltip } from "@similarweb/ui-components";

import leadsSearchFiltersI18nKeys from "app/pages/sales-intelligence/modules/leads-search-filters/utils/i18n";
import type { IndustryCodes } from "app/pages/sales-intelligence/types";

import { isIndustryCodeBlockedByOtherCondition } from "./isIndustryCodeBlockedByOtherCondition";
import {
    AUTOCOMPLETE_ITEM_HEIGHT,
    StyledAutocomplete,
    StyledItemsContainer,
    StyledListItem,
    StyledListItemText,
} from "./styles";

interface IProps {
    value: readonly string[];
    disabledCodes?: readonly string[];
    onUpdate: (value: readonly string[], isSelected: boolean) => void;
    codes: readonly IndustryCodes[];
    onFocus?: () => void;
}

export const AutocompleteCodes: React.FC<IProps> = ({ value, disabledCodes = [], onUpdate, codes, onFocus }) => {
    const { translate } = useTranslate();

    const autocompleteRef = useRef<Autocomplete>(null);
    const [index, setIndex] = useState(0);
    const scrollAreaRef = useRef<HTMLDivElement | null>(null);
    const listItems = useRef<IndustryCodes[]>([]);
    const prevInputValue = useRef("");
    const [isFocused, setIsFocused] = useState(false);

    const handleUpdate = useCallback(
        (codeToUpdate: string) => {
            if (isIndustryCodeBlockedByOtherCondition(codeToUpdate, value, disabledCodes)) {
                return;
            }

            setIndex(listItems.current.findIndex((item) => item.code === codeToUpdate));

            const isSelected = value.includes(codeToUpdate);

            onUpdate(isSelected ? value.filter((code) => code !== codeToUpdate) : [...value, codeToUpdate], isSelected);
        },
        [disabledCodes, onUpdate, value],
    );

    const getListItems = useCallback(
        (inputValue: string) => {
            if (prevInputValue.current !== inputValue) {
                setIndex(0);
            }

            prevInputValue.current = inputValue;
            if (!inputValue) {
                listItems.current = [];
                return [];
            }

            const filteredItems = codes.filter((item) => item.code.startsWith(inputValue));

            listItems.current = filteredItems;
            return filteredItems;
        },
        [codes],
    );

    const renderItems = ({
        listItems,
        query,
    }: {
        listItems: readonly IndustryCodes[];
        query: string;
    }): ReactElement | null => {
        const className = listItems.length > 0 ? "ListItemsContainer" : "";

        if (query && !listItems.length) {
            return null;
        }

        return (
            <div className={className}>
                <StyledItemsContainer ref={scrollAreaRef}>
                    {listItems.map((item) => {
                        const isSelected = value.includes(item.code);
                        const isBlockedByOtherCondition = isIndustryCodeBlockedByOtherCondition(
                            item.code,
                            value,
                            disabledCodes,
                        );
                        const isChecked = isSelected || isBlockedByOtherCondition;

                        return (
                            <StyledListItem
                                key={item.code}
                                $isDisabled={isBlockedByOtherCondition}
                                onClick={() => {
                                    if (isBlockedByOtherCondition) {
                                        return;
                                    }

                                    handleUpdate(item.code);
                                }}
                            >
                                <Tooltip
                                    placement="top"
                                    content={`${item.code} - ${item.label}`}
                                >
                                    <StyledListItemText>
                                        {item.code} - {item.label}
                                    </StyledListItemText>
                                </Tooltip>
                                <Checkbox
                                    selected={isChecked}
                                    isDisabled={isBlockedByOtherCondition}
                                />
                            </StyledListItem>
                        );
                    })}
                </StyledItemsContainer>
            </div>
        );
    };

    useEffect(() => {
        const handleBodyClick = (e: MouseEvent) => {
            if (!(e.target as HTMLElement).closest(".autocomplete-industry-codes")) {
                autocompleteRef.current?.truncateResults(true);

                // @ts-expect-error we knowingly access a private property
                autocompleteRef.current?.textFieldRef.clearValue();
                setIsFocused(false);
            }
        };
        document.body.addEventListener("click", handleBodyClick, { capture: true });

        return () => {
            document.body.removeEventListener("click", handleBodyClick, { capture: true });
        };
    }, []);

    const handleArrowKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === "ArrowDown") {
            setIndex((prevIndex) => (prevIndex >= listItems.current.length ? listItems.current.length : prevIndex + 1));
            scrollAreaRef?.current?.scrollTo({
                top: scrollAreaRef.current.scrollTop + AUTOCOMPLETE_ITEM_HEIGHT,
            });
        }
        if (e.key === "ArrowUp") {
            setIndex((prevIndex) => (prevIndex <= 0 ? 0 : prevIndex - 1));
            scrollAreaRef?.current?.scrollTo({
                top: scrollAreaRef.current.scrollTop - AUTOCOMPLETE_ITEM_HEIGHT,
            });
        }
    };

    const handleEnterKeyPress = () => {
        const item = listItems.current[index];
        if (item) {
            handleUpdate(item.code);
        }
    };

    const handleFocus = () => {
        onFocus?.();

        setIsFocused(true);
    };

    return (
        <StyledAutocomplete
            className="autocomplete-industry-codes"
            ref={autocompleteRef}
            getListItems={(inputValue: string) => getListItems(inputValue)}
            renderItems={renderItems}
            placeholder={translate(leadsSearchFiltersI18nKeys.industryFilterCodesAutocompletePlaceholder(isFocused))}
            floating
            debounce={300}
            preventTruncateUnlessForced
            onArrowKeyPress={handleArrowKeyPress}
            overrideEnterFunc={handleEnterKeyPress}
            onFocus={handleFocus}
            isFocused={isFocused}
            placeholderColor={colorsPalettes.carbon[200]}
            iconColor={colorsPalettes.carbon[200]}
        />
    );
};
